<template>
  <div class="page-settings">
    <div class="header">
      <div class="landing-page-settings">
        <div class="title">{{ pageSettingTitleKey ? $t(pageSettingTitleKey) : $t(`${i18nPrefix}.label.landingPageSettings`) }}</div>
        <el-switch
          v-model="customSetting.company_webpage_setting.is_activated"
          class="switch"
          active-color="#13ce66"
        />
      </div>
      <div v-if="!hiddenPageMenu" class="page-menu">
        <div class="label">{{ $t(`${i18nPrefix}.label.pageDisplayedName`) }}</div>
        <el-input v-model="customSetting.company_webpage_setting.webpage_menu" class="page-displayed-name"></el-input>
        <div v-if="webpageMenuError" class="err-msg">{{ $t(`${i18nPrefix}.label.msgInputMenuName`) }}</div>
      </div>
      <div v-if="isMobileBrandingPage" class="module-gap">
        <label class="label">{{ $t(`${i18nPrefix}.label.moduleGap`) }}</label>
        <el-switch
          v-model="customSetting.company_webpage_setting.show_gap"
          class="switch"
          active-color="#13ce66"
        />
      </div>
    </div>
    <div class="setting-content">
      <Banner
        v-if="customSetting.show_banner"
        ref="banner"
        :bannerSetting="customSetting.company_webpage_setting.pc_branding_banner"
      />
      <MobileHeader
        v-if="customSetting.show_header_image"
        ref="mobileHeader"
        :webpageSetting="customSetting.company_webpage_setting"
        :canEdit="customSetting.edit_header_image"
        :company="customSetting.company"
      />
      <CustomContentEditor
        ref="customContent"
        :showContentHeadTitle="showContentHeadTitle"
        :showAddText="showAddText"
        :showAddPictureBoard="showAddPictureBoard"
        :contentList="customSetting.company_webpage_setting.items"
        :showContentTitle="!isPcSetting"
        :showHiddenPaddingCheckbox="isMobileBrandingPage"
        :jobDescriptionTipKey="jobDescriptionTipKey"
      />
      <div v-if="hasVideoNavigationPermission && !isPcSetting && !hiddenVideoNavigation" class="video-navigation">
        <div class="title">{{ $t(`${i18nPrefix}.label.videoNavigation`) }}</div>
        <VideoNavigationEditor
          class="video-navigation-editor"
          ref="videoNavigationEditor"
          :rootCompanyId="customSetting.company.root_company_id"
          :languageCode="customSetting.language"
          :data="customSetting.company_webpage_setting.video_navigation_list"
        ></VideoNavigationEditor>
      </div>
    </div>
  </div>
</template>

<script>
import Banner from './banner.vue'
import CustomContentEditor from '@/components/job/editor/customContentEditor.vue'
import MobileHeader from './mobileHeader.vue'
import VideoNavigationEditor from '@/components/job/editor/videoNavigationEditor.vue'
import { MOBILE_PAGE_NUM_MAP, PC_PAGE_NUM_LIST } from '@/constant/company'
import _ from 'lodash'

export default {
  props: {
    customSetting: {
      type: Object,
      require: true
    },
    hiddenVideoNavigation: {
      type: Boolean,
      default: false
    },
    showAddText: {
      type: Boolean,
      default: true
    },
    showAddPictureBoard: {
      type: Boolean,
      default: true
    },
    hiddenPageMenu: {
      type: Boolean,
      default: false
    },
    pageSettingTitleKey: {
      type: String,
      default: ''
    },
    showContentHeadTitle: {
      type: Boolean,
      default: true
    },
    jobDescriptionTipKey: {
      type: String,
      default: ''
    }
  },
  components: {
    Banner,
    CustomContentEditor,
    MobileHeader,
    VideoNavigationEditor
  },
  data () {
    return {
      i18nPrefix: 'admin.company.customLandingPage',
      pageDisplayedName: null,
      webpageMenuError: false
    }
  },
  computed: {
    hasVideoNavigationPermission () {
      return this.hasAnyPermission('PREMIUM_VIDEO_NAVIGATION')
    },
    isPcSetting () {
      const pageNum = this.$route.query.page_num
      return PC_PAGE_NUM_LIST.includes(pageNum)
    },
    isMobileBrandingPage () {
      return _.includes(_.values(MOBILE_PAGE_NUM_MAP), this.$route.query.page_num)
    }
  },
  created () {

  },
  methods: {
    async submit () {
      this.webpageMenuError = false
      if (!this.hiddenPageMenu && !this.customSetting.company_webpage_setting.webpage_menu) {
        this.webpageMenuError = true
        this.$nextTick(() => {
          const errEl = this.$el.querySelector('.err-msg')
          if (errEl) {
            errEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
        return false
      }

      let hasError = false
      const promiseList = _.map({
        pcBrandingBanner: this.$refs.banner,
        mobileHeader: this.$refs.mobileHeader,
        customContentList: this.$refs.customContent,
        videoNavigationList: this.$refs.videoNavigationEditor
      }, (contentRef, settingName) => {
        return contentRef?.submit().then(data => {
          if (data) {
            this.$set(this.customSetting, settingName, data)
          } else {
            hasError = true
          }
        })
      })
      await Promise.all(promiseList)
      return !hasError
    }
  }
}
</script>

<style lang="scss" scoped>
.page-settings {
  .header {
    margin-top: 20px;

    .landing-page-settings {
      display: flex;

      .title {
        font-size: 20px;
        margin-right: auto;
      }
    }

    .page-menu {
      margin-top: 20px;
      position: relative;
      display: flex;
      align-items: center;

      .label {
        margin-right: 35px;
      }

      .page-displayed-name {
        flex: 1;
        ::v-deep .el-input__inner {
          border-radius: 0;
          background: #f7f7f7;
          padding: 0 20px;
        }
      }

      .err-msg {
        position: absolute;
        left: 0;
        top: 100%;
        font-size: 12px;
        color: #F56C6C;
      }
    }
  }

  .module-gap {
    margin-top: 20px;
    height: 32px;
    display: flex;
    align-items: center;

    .label {
      margin-right: auto;
    }
  }

  .setting-content {
    .video-navigation {
      margin-top: 20px;
      padding-top: 15px;
      border-top: 1px solid #ddd;

      .title {
        font-size: 16px;
        margin-bottom: 15px;
      }
    }

    .video-navigation-editor {
      ::v-deep .video-container {
        .video-item {
          .video {
            width: 195px;
          }
        }
      }
    }
  }

  ::v-deep .el-input__inner {
    height: 32px;
    line-height: 32px;
    padding: 0 5px;
    border-radius: 0;
    color: #232323;
  }

  ::v-deep .el-input-group__prepend {
    border-radius: 0;
    padding: 0 10px;
  }
}
</style>
